import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI } from '../../api';

const Container = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

const ActivityList = styled.ul`
  list-style: none;
  padding: 0;
`;

const ActivityItem = styled.li`
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: flex-start;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.2rem;
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.h3`
  font-size: 1rem;
  color: #111827;
  margin: 0 0 0.25rem 0;
`;

const ActivityDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
`;

const ActivityTime = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
  display: block;
  margin-top: 0.25rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
`;

// 페이지네이션 컴포넌트 추가
const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  gap: 0.5rem;
`;

const PageButton = styled.button<{ isActive?: boolean }>`
  width: 2rem;
  height: 2rem;
  border-radius: 0.25rem;
  background-color: ${props => props.isActive ? '#ef4444' : 'white'};
  color: ${props => props.isActive ? 'white' : '#4b5563'};
  border: 1px solid ${props => props.isActive ? '#ef4444' : '#d1d5db'};
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.isActive ? '#dc2626' : '#f3f4f6'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  color: #b91c1c;
  padding: 1rem;
  border-radius: 0.25rem;
  margin-bottom: 1rem;
`;

interface Activity {
  id: number;
  activity_type: string;
  title: string;
  description: string;
  created_at: string;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

const Activities: React.FC = () => {
  const { user } = useAuth();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0
  });
  
  const fetchActivities = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      try {
        const response = await userAPI.getActivities(page, 10);
        
        if (response.data && response.data.success) {
          setActivities(response.data.activities || []);
          setPagination({
            currentPage: response.data.pagination.currentPage,
            totalPages: response.data.pagination.totalPages,
            totalItems: response.data.pagination.totalItems
          });
        } else {
          throw new Error(response.data.message || '활동 내역을 불러오는데 실패했습니다.');
        }
      } catch (err) {
        console.error('API 호출 실패, 임시 데이터 사용:', err);
        
        // API가 실패한 경우 임시 데이터 사용
        setActivities([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0
        });
      }
    } catch (err) {
      console.error('활동내역 불러오기 오류:', err);
      setError('활동 내역을 불러오는데 문제가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchActivities(1);
  }, []);
  
  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    fetchActivities(page);
  };
  
  // 활동 유형에 따른 아이콘 선택
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'post_create':
        return '📝';
      case 'comment_create':
        return '💬';
      case 'login':
        return '🔑';
      case 'board_view':
        return '📋';
      case 'attendance_check':
        return '✓';
      case 'profile_update':
        return '👤';
      case 'point_earn':
        return '🎖️';
      default:
        return '📋';
    }
  };
  
  // 날짜 포맷팅
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  if (loading) {
    return <Container>데이터를 불러오는 중입니다...</Container>;
  }
  
  return (
    <Container>
      <Title>활동 내역</Title>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {activities.length === 0 ? (
        <EmptyState>아직 활동 내역이 없습니다.</EmptyState>
      ) : (
        <>
          <ActivityList>
            {activities.map(activity => (
              <ActivityItem key={activity.id}>
                <ActivityIcon>{getActivityIcon(activity.activity_type)}</ActivityIcon>
                <ActivityContent>
                  <ActivityTitle>{activity.title}</ActivityTitle>
                  <ActivityDescription>{activity.description}</ActivityDescription>
                  <ActivityTime>{formatDate(activity.created_at)}</ActivityTime>
                </ActivityContent>
              </ActivityItem>
            ))}
          </ActivityList>
          
          {pagination.totalPages > 1 && (
            <Pagination>
              <PageButton 
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                &lt;
              </PageButton>
              
              {Array.from({ length: pagination.totalPages }).map((_, index) => (
                <PageButton
                  key={index + 1}
                  isActive={pagination.currentPage === index + 1}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </PageButton>
              ))}
              
              <PageButton 
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                &gt;
              </PageButton>
            </Pagination>
          )}
        </>
      )}
    </Container>
  );
};

export default Activities; 